import { isRecipientInvitedWireStatus } from '@/helpers/wireHelpers';
import { UserDetailsWidget } from '@/bundle/shared/components/UserDetailsWidget/UserDetailsWidget';
import { DetailsEditButton } from '@/bundle/_OrgAdmin/shared/ui/DetailsEditButton/DetailsEditButton';
import { ReInvitationDetails } from '@/bundle/shared/components/UserDetailsWidget/ReInvitationDetails';
import { AssignedWireType } from '@/types/wireTypes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { reinviteRecipientByProxy } from '@/bundle/shared/components/RecipientDetails/api';
import { useState } from 'react';
import { showSnackbarResponseError } from '@/helpers/helpers';

type RecipientChainProxyDetailsType = {
  wire: AssignedWireType;
  isProxyEditRecipient?: boolean;
  onProxyEditRecipient?: () => void;
  refetchOutboundWire?: () => void;
};

export const RecipientChainProxyDetails = ({
  wire,
  isProxyEditRecipient,
  onProxyEditRecipient,
}: RecipientChainProxyDetailsType) => {
  const queryClient = useQueryClient();
  const proxyRecipientDetails = wire?.assigned_proxy_user;
  const recipientDetails = wire?.assigned_user;
  const isDocumentVerificationRequired = !!wire?.is_pd_provider_kyc_required;
  const isRecipientInvited = isRecipientInvitedWireStatus(wire?.status);

  const [isOpenModal, setIsOpenModal] = useState(false);

  const refetchRecipientWire = () => {
    queryClient.invalidateQueries({ queryKey: ['load_recipient_wire', wire?.id] });
  };

  const { mutate: reinviteRecipientByProxyMutate } = useMutation({
    mutationKey: ['reinvite_recipient_by_proxy'],
    mutationFn: () => {
      return reinviteRecipientByProxy(wire?.id);
    },
    onSuccess(response) {
      if (response.error) {
        refetchRecipientWire();
        setIsOpenModal(false);

        return showSnackbarResponseError(response?.error);
      }

      refetchRecipientWire();
      setIsOpenModal(false);
    },
  });

  return (
    <>
      <UserDetailsWidget header='Recipient Proxy' user={proxyRecipientDetails} />
      {!isProxyEditRecipient && (
        <UserDetailsWidget
          header='Recipient'
          user={recipientDetails}
          action={isRecipientInvited && <DetailsEditButton onEdit={onProxyEditRecipient} />}
          marker={isDocumentVerificationRequired && 'ID VERIFICATION'}
          hasInvitedNotification={isRecipientInvited}
        >
          {isRecipientInvited && (
            <ReInvitationDetails
              onConfirm={reinviteRecipientByProxyMutate}
              invitationDetails={recipientDetails?.invitation_data}
              onOpen={() => setIsOpenModal(true)}
              onClose={() => setIsOpenModal(false)}
              isOpen={isOpenModal}
              onRefetchWire={refetchRecipientWire}
            />
          )}
        </UserDetailsWidget>
      )}
    </>
  );
};
